import React, { useState, useEffect, useRef } from 'react';
import {
  Typography, Box, Chip, Button, Dialog, DialogTitle, DialogContent,
  Grid, IconButton, Alert, Card, CardContent
} from '@mui/material';
import NfcIcon from '@mui/icons-material/Nfc';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DialpadIcon from '@mui/icons-material/Dialpad';
import BackspaceIcon from '@mui/icons-material/Backspace';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import supabase from '../../supabaseClient';
import WorkerCameraScanner from './WorkerCameraScanner';
import Divider from '@mui/material/Divider';
import { useMediaQuery, useTheme } from '@mui/material';
import NoticeboardForWorker from '../Manager/Home/Calendar/Noticeboard/NoticeboardForWorker';

// ── Fullscreen success/error overlay ─────────────────────────────────────────
function ScanOverlay({ state, msg }) {
  if (!state) return null;
  const ok = state === 'success';
  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 1200,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: ok
        ? 'radial-gradient(ellipse at center, #d0f0e0 0%, #b8e8cc 100%)'
        : 'radial-gradient(ellipse at center, #f5d0d0 0%, #e8b8b8 100%)',
      animation: 'fadeIn 0.25s ease',
      '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
    }}>
      <Box sx={{
        width: 140, height: 140, borderRadius: '50%',
        bgcolor: ok ? 'rgba(76,175,80,0.15)' : 'rgba(198,40,40,0.12)',
        border: `3px solid ${ok ? '#4caf50' : '#c62828'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4,
      }}>
        {ok
          ? <CheckIcon sx={{ fontSize: 72, color: '#4caf50' }} />
          : <CloseIcon sx={{ fontSize: 72, color: '#c62828' }} />}
      </Box>
      <Typography variant="h3" sx={{ fontWeight: 800, color: ok ? '#1b5e20' : '#7f0000', mb: 1 }}>
        {ok ? 'Skanowanie poprawne!' : 'Błąd odczytu!'}
      </Typography>
      <Typography variant="h6" sx={{ color: ok ? '#2e7d32' : '#b71c1c', fontWeight: 400 }}>
        {ok ? 'Miłego dnia.' : 'Skontaktuj się z przełożonym.'}
      </Typography>
      {msg ? <Typography variant="body2" sx={{ color: ok ? '#388e3c' : '#c62828', mt: 1, px: 4, textAlign: 'center' }}>{msg}</Typography> : null}
    </Box>
  );
}

// ── Pulsing NFC ring ──────────────────────────────────────────────────────────
function NfcRing({ isScanning, onClick }) {
  return (
    <Box onClick={onClick} sx={{
      width: 220, height: 220, mx: 'auto',
      borderRadius: '50%', cursor: isScanning ? 'default' : 'pointer',
      position: 'relative',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      '@keyframes pulseRing': {
        '0%':   { transform: 'scale(1)',    opacity: 0.5 },
        '50%':  { transform: 'scale(1.1)', opacity: 0.2 },
        '100%': { transform: 'scale(1)',    opacity: 0.5 },
      },
    }}>
      <Box sx={{
        position: 'absolute', inset: -28, borderRadius: '50%',
        border: '3px solid rgba(0,0,0,0.1)',
        animation: 'pulseRing 2.2s ease-in-out infinite',
      }} />
      <Box sx={{
        position: 'absolute', inset: -12, borderRadius: '50%',
        border: '2px solid rgba(0,0,0,0.07)',
      }} />
      <Box sx={{
        width: 220, height: 220, borderRadius: '50%',
        bgcolor: '#f0ede8',
        boxShadow: '0 6px 36px rgba(0,0,0,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <NfcIcon sx={{ fontSize: 96, color: isScanning ? '#1976d2' : '#666' }} />
      </Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function WorkerAttendanceGateKeeper() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [idConfig,    setIdConfig]    = useState(null);
  const [isScanning,  setIsScanning]  = useState(false);
  const [overlay,     setOverlay]     = useState(null); // 'success' | 'error' | null
  const [overlayMsg,  setOverlayMsg]  = useState('');
  const [recentLogs,  setRecentLogs]  = useState([]);
  const [selectedDate,setSelectedDate]= useState(new Date().toISOString().split('T')[0]);
  const [clock,       setClock]       = useState('');
  const [pinInput,    setPinInput]    = useState('');
  const [pinError,    setPinError]    = useState('');

  // ── Confirmation popup state ──
  const [confirmOpen,         setConfirmOpen]         = useState(false);
  const [confirmEmployee,     setConfirmEmployee]     = useState(null);
  const [confirmActiveRecord, setConfirmActiveRecord] = useState(null); // null = brak check-in, obiekt = trwa praca
  const [confirmHoursToday,   setConfirmHoursToday]   = useState(0);    // suma minut przepracowanych dziś
  const [activeMachineName,   setActiveMachineName]   = useState('');   // Nazwa aktywnej maszyny (jeśli występuje)

  const cameraRef  = useRef();
  const historyRef = useRef();

  // Clock
  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  // Init
  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data } = await supabase.from('profiles').select('id_configuration').eq('id', session.user.id).single();
        if (data) setIdConfig(data.id_configuration);
      }
    };
    init();
  }, []);

  useEffect(() => { if (idConfig) fetchLogs(idConfig, selectedDate); }, [idConfig, selectedDate]);

  // Auto-close popupu po 15 sekundach bezczynności
  useEffect(() => {
    if (!confirmOpen) return;
    const timer = setTimeout(() => handleConfirmCancel(), 15000);
    return () => clearTimeout(timer);
  }, [confirmOpen]);

  const showOverlay = (type, msg = '') => {
    setOverlay(type); setOverlayMsg(msg);
    setTimeout(() => { setOverlay(null); setOverlayMsg(''); }, 3000);
  };

  const fetchLogs = async (cfg, date) => {
    const { data } = await supabase.from('attendance')
      .select('*, employees(name, surname)')
      .eq('id_configuration', cfg).eq('type', 'work')
      .gte('check_in', `${date}T00:00:00Z`)
      .lte('check_in', `${date}T23:59:59Z`)
      .order('check_in', { ascending: false });
    if (data) setRecentLogs(data);
  };

  const playSuccessSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc.start(); osc.stop(ctx.currentTime + 0.2);
    } catch (e) {}
  };

  const playClickSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); 
      const gain = ctx.createGain();
      
      osc.connect(gain); 
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
      
      osc.start(); 
      osc.stop(ctx.currentTime + 0.05);
    } catch (e) {
      console.error("Audio error:", e);
    }
  };

  const executeAttendanceLogging = async (emp) => {
    const { data: lastRec } = await supabase.from('attendance')
      .select('check_in, check_out').eq('employee_id', emp.id).eq('type', 'work')
      .order('check_in', { ascending: false }).limit(1).maybeSingle();

    if (lastRec) {
      const diff = (Date.now() - new Date(lastRec.check_out || lastRec.check_in).getTime()) / 1000;
      if (diff < 60) throw new Error(`${emp.name}! Odczekaj jeszcze ${Math.ceil(60 - diff)} sek.`);
    }

    const { data: activeRecords } = await supabase.from('attendance')
      .select('id').eq('employee_id', emp.id).eq('type', 'work').is('check_out', null)
      .order('check_in', { ascending: false }).limit(1);

    const active = activeRecords?.length > 0 ? activeRecords[0] : null;
    const action = active ? 'WYJSCIE' : 'WEJSCIE';
    const now = new Date();

    // ── Zamykanie otwartych maszyn przy wyjściu z pracy ──
    if (active) {
      const { data: deviceLogs } = await supabase.from('device_attendance')
        .select('device_id, type, stand_id')
        .eq('attendance_id', active.id)
        .order('timestamp', { ascending: true });

      if (deviceLogs?.length > 0) {
        // Obliczamy ostatni status dla każdej maszyny
        const deviceStatusMap = {};
        const deviceStandMap = {};
        
        deviceLogs.forEach(log => { 
          deviceStatusMap[log.device_id] = log.type;
          if (log.stand_id) deviceStandMap[log.device_id] = log.stand_id;
        });

        // Znajdujemy maszyny z aktywnym 'START_WORK'
        const openDeviceIds = Object.keys(deviceStatusMap).filter(id => deviceStatusMap[id] === 'START_WORK');

        // Automatycznie wstawiamy STOP_WORK dla każdej otwartej maszyny
        for (const devId of openDeviceIds) {
          await supabase.from('device_attendance').insert([{
            employee_id: emp.id,
            device_id: devId,
            attendance_id: active.id,
            type: 'STOP_WORK',
            timestamp: now.toISOString(),
            id_configuration: idConfig,
            stand_id: deviceStandMap[devId] || null
          }]);
        }
      }
    }

    let photoPath = null;
    if (cameraRef.current) {
      const blob = await cameraRef.current.capture();
      if (blob) {
        const path = `${emp.id}/${now.toISOString().split('T')[0]}/${action}_${Date.now()}.jpg`;
        const { data: st } = await supabase.storage.from('attendance-photos').upload(path, blob);
        photoPath = st?.path;
      }
    }

    if (active) {
      await supabase.from('attendance').update({ check_out: now.toISOString(), photo_exit_url: photoPath }).eq('id', active.id);
    } else {
      await supabase.from('attendance').insert([{
        employee_id: emp.id, id_configuration: idConfig,
        check_in: now.toISOString(), photo_entry_url: photoPath, type: 'work'
      }]);
    }

    playSuccessSound();
    if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    const timeStr = now.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    return `Odbito ${action}: ${emp.name} ${emp.surname} o ${timeStr}`;
  };

  // ── Helpers do popupu z potwierdzeniem ──
  const calculateTodayMinutes = (records) => {
    const now = Date.now();
    return records.reduce((total, rec) => {
      const start = new Date(rec.check_in).getTime();
      const end = rec.check_out ? new Date(rec.check_out).getTime() : now;
      return total + (end - start) / 1000 / 60;
    }, 0);
  };

  const prepareAttendanceConfirmation = async (emp) => {
    const today = new Date().toISOString().split('T')[0];
    const { data: todayRecords } = await supabase.from('attendance')
      .select('id, check_in, check_out')
      .eq('employee_id', emp.id)
      .eq('type', 'work')
      .gte('check_in', `${today}T00:00:00Z`)
      .lte('check_in', `${today}T23:59:59Z`)
      .order('check_in', { ascending: false });

    const active = todayRecords?.find(r => !r.check_out) || null;
    const minutes = calculateTodayMinutes(todayRecords || []);

    let openMachineName = '';
    if (active) {
      const { data: deviceLogs } = await supabase.from('device_attendance')
        .select('type, device_id')
        .eq('attendance_id', active.id)
        .order('timestamp', { ascending: true });

      if (deviceLogs?.length > 0) {
        const deviceStatusMap = {};
        deviceLogs.forEach(log => { deviceStatusMap[log.device_id] = log.type; });
        const openId = Object.keys(deviceStatusMap).find(k => deviceStatusMap[k] === 'START_WORK');
        if (openId) {
          const { data: dev } = await supabase.from('devices').select('name').eq('id', openId).maybeSingle();
          if (dev) openMachineName = dev.name;
        }
      }
    }

    setConfirmEmployee(emp);
    setConfirmActiveRecord(active);
    setConfirmHoursToday(minutes);
    setActiveMachineName(openMachineName);
    setConfirmOpen(true);
  };

  const handleConfirmAttendance = async () => {
    if (!confirmEmployee) return;
    const emp = confirmEmployee;
    setConfirmOpen(false);
    setConfirmEmployee(null);
    setConfirmActiveRecord(null);
    setConfirmHoursToday(0);
    setActiveMachineName('');

    try {
      const msg = await executeAttendanceLogging(emp);
      showOverlay('success', msg);
      fetchLogs(idConfig, selectedDate);
    } catch (e) {
      showOverlay('error', e.message || 'Błąd zapisu');
    }
  };

  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    setConfirmEmployee(null);
    setConfirmActiveRecord(null);
    setConfirmHoursToday(0);
    setActiveMachineName('');
  };

  const startNfc = async () => {
    // if (!('NDEFReader' in window)) { showOverlay('error', 'Brak NFC w tym urządzeniu'); return; }
    if (!('NDEFReader' in window)) { return; }
    try {
      const ndef = new window.NDEFReader();
      await ndef.scan();
      setIsScanning(true);
      ndef.onreading = ({ serialNumber }) => handleRecord(serialNumber);
    } catch (e) { /* showOverlay('error', 'Błąd czytnika NFC'); */ }
  };

  const handleRecord = async (serial) => {
    try {
      const { data: emp } = await supabase.from('employees')
        .select('id, name, surname, id_configuration')
        .eq('nfc_card_id', serial.toLowerCase().trim())
        .maybeSingle();
      if (!emp || emp.id_configuration !== idConfig) {
        showOverlay('error', 'Nieznana karta');
        return;
      }
      await prepareAttendanceConfirmation(emp);
    } catch (e) {
      showOverlay('error', e.message || 'Błąd odczytu karty');
    }
  };

  const handleRecordByPin = async (typedPin) => {
    if (!typedPin) return;
    setPinError('');
    try {
      const { data: emp, error } = await supabase.from('employees')
        .select('id, name, surname, id_configuration')
        .eq('pin', typedPin.trim())
        .maybeSingle();
      if (error || !emp || emp.id_configuration !== idConfig) {
        setPinError('Nieprawidłowy kod PIN!');
        setPinInput('');
        return;
      }
      setPinInput('');
      await prepareAttendanceConfirmation(emp);
    } catch (e) {
      setPinError(e.message || 'Błąd autoryzacji PIN');
      setPinInput('');
    }
  };

  const handleKeyPress = (num) => { 
    playClickSound(); 
    setPinError(''); 
    if (pinInput.length < 8) setPinInput(p => p + num); 
  };
  
  const handleBackspace = () => {
    playClickSound(); 
    setPinInput(p => p.slice(0, -1));
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: '#f5f2ed', display: 'flex', flexDirection: 'column' }}>
      <ScanOverlay state={overlay} msg={overlayMsg} />

      {/* Hidden camera */}
      <Box sx={{ display: 'none' }}><WorkerCameraScanner ref={cameraRef} /></Box>

      {/* Komponent ogłoszeń */}
      <Box sx={{ mt: 4 }}>
        <NoticeboardForWorker profileType="worker" idConfig={idConfig}/>
      </Box>

      {/* ── Główny panel ── */}
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: 1300,
        mx: 'auto',
        pt: isMobile ? 2 : 6,
        pb: 2,
        px: 2,
        gap: isMobile ? 2 : 0
      }}>

        {/* ── Zegar — wspólny na mobile ── */}
        {isMobile && (
          <Typography variant="h2" sx={{
            fontWeight: 200, color: '#444', letterSpacing: 6,
            fontSize: '3rem', mb: 1
          }}>
            {clock}
          </Typography>
        )}

        {/* ── NFC panel ── */}
        {!isMobile && (
          <Box sx={{
            width: 480,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography variant="h2" sx={{ fontWeight: 200, color: '#444', mb: 6, letterSpacing: 6 }}>
              {clock}
            </Typography>
            <NfcRing isScanning={isScanning} onClick={!isScanning ? startNfc : null} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#222', mt: 5, mb: 0.5 }}>
              {isScanning ? 'Gotowy!' : 'Witaj!'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#999', mb: 5, fontSize: '1.1rem' }}>
              Przyłóż kartę NFC do czytnika
            </Typography>
          </Box>
        )}

        {/* ── Divider — tylko na desktopie ── */}
        {!isMobile && <Divider orientation="vertical" flexItem sx={{ mx: 6, my: 8 }} />}

        {/* ── PIN panel ── */}
        <Box sx={{ width: isMobile ? 320 : 380, maxWidth: 380 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }}>Wprowadź kod PIN</Typography>
          </Box>

          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h3" sx={{
              letterSpacing: '10px', fontWeight: 700, minHeight: 60,
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              bgcolor: '#f5f5f5', borderRadius: 3, border: '1px solid #e0e0e0'
            }}>
              {'•'.repeat(pinInput.length)}
            </Typography>
            {pinError && <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }}>{pinError}</Alert>}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <Button key={n} fullWidth variant="contained" color="inherit"
                onClick={() => handleKeyPress(n.toString())}
                sx={{ py: 2.5, fontSize: '1.6rem', fontWeight: 700, borderRadius: 3, bgcolor: '#fafafa', boxShadow: 1 }}>
                {n}
              </Button>
            ))}
            <Button fullWidth variant="outlined" color="error" onClick={handleBackspace} sx={{ py: 2.5, borderRadius: 3 }}>
              <BackspaceIcon />
            </Button>
            <Button fullWidth variant="contained" color="inherit" onClick={() => handleKeyPress('0')}
              sx={{ py: 2.5, fontSize: '1.6rem', fontWeight: 700, borderRadius: 3, bgcolor: '#fafafa', boxShadow: 1 }}>
              0
            </Button>
            <Button fullWidth variant="contained" color="success" onClick={() => handleRecordByPin(pinInput)}
              disabled={pinInput.length === 0}
              sx={{ py: 2.5, fontSize: '1.1rem', fontWeight: 700, borderRadius: 3 }}>
              OK
            </Button>
          </Box>
        </Box>

      </Box>

      {/* ── Popup z potwierdzeniem (Start / Stop) ── */}
      <Dialog open={confirmOpen} onClose={handleConfirmCancel} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
          {confirmEmployee?.name} {confirmEmployee?.surname}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            Przepracowano dziś
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 3, color: '#222' }}>
            {Math.floor(confirmHoursToday / 60)}h {Math.round(confirmHoursToday % 60)}min
          </Typography>

          {confirmActiveRecord && (
            <Typography variant="body2" sx={{ color: '#2e7d32', mb: activeMachineName ? 1 : 3, fontWeight: 600 }}>
              Trwa zmiana od {new Date(confirmActiveRecord.check_in).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}

          {activeMachineName && (
            <Typography variant="caption" sx={{ color: '#ed6c02', display: 'block', mb: 3, fontWeight: 500 }}>
              Zakończenie pracy zamknie też stanowisko: "{activeMachineName}".
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button fullWidth variant="outlined" color="inherit" onClick={handleConfirmCancel}
              sx={{ py: 2, borderRadius: 3, fontWeight: 700 }}>
              Anuluj
            </Button>
            <Button fullWidth variant="contained"
              color={confirmActiveRecord ? 'error' : 'success'}
              onClick={handleConfirmAttendance}
              sx={{ py: 2, borderRadius: 3, fontWeight: 700, fontSize: '1.1rem' }}>
              {confirmActiveRecord ? 'STOP' : 'START'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

    </Box>
  );
}

export default WorkerAttendanceGateKeeper;